"use client";
import React, { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { cn } from "utils/utils";
import { Home } from "lucide-react";
import Link from "next/link";

type Props = {
  name?: string;
  className?: string;
  hardCodeDisable?: Record<number, boolean>;
  names?: Record<number, string>;
  hrefs?: Record<number, string>;
  shift?: boolean;
  maxSegments?: number;
};
const mobileIconSize = 14;
function generatePathNames(
  pathName: string,
  shift?: boolean,
  maxSegments?: number
): string[] {
  const paths = pathName.split("?")[0].split("/").filter(Boolean);
  if (shift) paths.splice(3, 1);
  return maxSegments === undefined ? paths : paths.slice(0, maxSegments);
}

function formatPathName(path: string): string {
  return path.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Breadcrumbs({
  shift,
  names,
  hrefs,
  className,
  name,
  hardCodeDisable,
  maxSegments,
}: Props) {
  const pathName = usePathname();
  const paths = generatePathNames(pathName, shift, maxSegments);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function generateLink(idx: number) {
    return hrefs?.[idx] ?? `/${paths.slice(0, idx + 1).join("/")}`;
  }

  // Get the current page name (last breadcrumb item)
  const currentPageIdx = paths.length - 1;
  const currentPageName =
    currentPageIdx >= 0
      ? formatPathName(names?.[currentPageIdx] || name || paths[currentPageIdx])
      : "";

  return (
    <nav
      className={cn(
        "relative mt-1 min-w-0 max-w-full text-xs md:text-base",
        className
      )}
      aria-label="Breadcrumb"
    >
      {/* Desktop view - full breadcrumb */}
      <ol className="flex min-w-0 max-w-full flex-row items-center">
        <li
          className={cn({
            "max-md:hidden": !(paths.length === 1),
          })}
        >
          <Link
            href="/"
            className="hover:text-primary-focus text-primary transition-colors duration-200"
            aria-label="Home"
          >
            <Home className="h-4 w-4" />
          </Link>
        </li>
        {paths.map((path, idx) => {
          const isLast = idx === paths.length - 1;
          const displayText = formatPathName(names?.[idx] || name || path);
          const disableLink = isLast || hardCodeDisable?.[idx];

          if (path === "x") {
            return <span key={idx}></span>;
          }

          return (
            <li
              key={idx}
              className={cn(
                "flex items-center",
                isLast ? "min-w-0" : "shrink-0",
                !isLast && idx !== currentPageIdx - 1 && "max-md:hidden"
              )}
            >
              <span className="text-base-content/40 mx-1">
                {idx === currentPageIdx - 1 ? (
                  <>
                    <span className="md:hidden">../</span>
                    <span className="max-md:hidden">/</span>
                  </>
                ) : (
                  "/"
                )}
              </span>
              {disableLink ? (
                <span className="text-base-content truncate">
                  {displayText}
                </span>
              ) : (
                <Link
                  href={generateLink(idx)}
                  className="hover:text-primary-focus text-primary transition-colors duration-200"
                >
                  {displayText}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      {/* Mobile view - dropdown */}
      {/*<div className="my-1 ml-2 sm:hidden" ref={dropdownRef}>
        <button
          className="flex items-center gap-1 text-primary"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
        >
          <Home className="mr-1" size={mobileIconSize} />
          <span className="max-w-[200px] truncate text-xs">
            {currentPageName}
          </span>
          <ChevronDown
            size={mobileIconSize}
            className={cn(
              "transition-transform duration-200",
              isDropdownOpen ? "rotate-180" : ""
            )}
          />
        </button>

        {isDropdownOpen && (
          <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-base-300 bg-base-100 p-2 shadow-lg">
            <nav className="space-y-1">
              <li className="flex items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-base-200">
                <Link
                  href="/"
                  className="flex w-full items-center text-xs text-primary"
                >
                  <Home size={mobileIconSize} className="mr-2" />
                  Home
                </Link>
              </li>
              {paths.map((path, idx) => {
                const displayText = formatPathName(
                  names?.[idx] || name || path
                );
                const isLast = idx === paths.length - 1;
                const disableLink = isLast || hardCodeDisable?.[idx];

                return (
                  <li
                    key={idx}
                    className={cn(
                      "flex items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-base-200",
                      {
                        "pointer-events-none": disableLink,
                      }
                    )}
                  >
                    <ChevronRight
                      size={mobileIconSize}
                      className={cn("shrink-0", {
                        "text-primary": !disableLink,
                      })}
                    />
                    {disableLink ? (
                      <span className="ml-2 text-xs font-medium text-base-content">
                        {displayText}
                      </span>
                    ) : (
                      <Link
                        href={generateLink(idx)}
                        className="ml-2 w-full overflow-hidden text-ellipsis text-xs text-primary"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        {displayText}
                      </Link>
                    )}
                  </li>
                );
              })}
            </nav>
          </div>
        )}
      </div>*/}
    </nav>
  );
}
